<template>
  <div style="margin-top:20px">
    <FiPageRail :title="pageTitle" nowrapActions>
      <template #actions>
        <b-button size="sm" variant="outline-secondary" @click="showInfo = true" title="About this page" aria-label="About this page">
          <i class="fa fa-info-circle"></i>
        </b-button>
        <b-button v-if="canManageSettings" size="sm" variant="outline-secondary" @click="showSettings = true" title="Bus SOS settings" aria-label="Bus SOS settings">
          <i class="fa fa-cog"></i>
        </b-button>
      </template>
    </FiPageRail>

    <b-modal v-model="showInfo" :title="'About ' + pageTitle" ok-only ok-title="Close">
      <p v-if="direction === 'received'">SOS requests <strong>your company received</strong> from other companies (broadcast to you because you have a yard or vehicle in range). Each row shows which company sent it, when, and the current status.</p>
      <p v-else-if="direction === 'sent'">SOS requests <strong>your company raised</strong>. Each row shows the vehicle, when, who was notified, how many nearby vehicles and yards were found, and (if broadcast) which companies it went to.</p>
      <p v-else>A log of every Bus SOS request. Each row shows who raised it, when, who was notified, and how many nearby vehicles and yards were found.</p>
      <ul>
        <li>Click a row to open the full detail — including the exact email and SMS that were sent.</li>
        <li v-if="direction === 'received'">These SOS were raised by another company — only they can change the status. You can <strong>remove</strong> one from your own list once you're done with it; the sending company still keeps theirs.</li>
        <li v-else>Use the action icons to <strong>close</strong>, <strong>cancel</strong>, or <strong>delete</strong> a request.</li>
        <li>Use the date chips to switch between the last 6 months and all time.</li>
      </ul>
    </b-modal>

    <sos-settings-modal v-model="showSettings" @showAlert="forward_alert" />
    <div class="sos-history-body">
      <div class="sos-history-toolbar">
        <span class="sos-history-label">Date Range</span>
        <div class="sos-chips">
          <button type="button" class="sos-chip" :class="{ 'sos-chip--active': range === 'recent' }" @click="set_range('recent')">Last 6 Months</button>
          <button type="button" class="sos-chip" :class="{ 'sos-chip--active': range === 'all' }" @click="set_range('all')">All</button>
        </div>
        <span class="sos-history-label">Status</span>
        <div class="sos-chips">
          <button type="button" class="sos-chip" :class="{ 'sos-chip--active': statusFilter === s.key }"
                  v-for="s in statusChips" :key="s.key" @click="set_status(s.key)">
            {{ s.label }} <span class="sos-chip-count">{{ statusCounts[s.key] }}</span>
          </button>
        </div>
      </div>
      <!-- desktop: table (styled to match the ERM Log) -->
      <div class="d-none d-md-block sos-table">
        <b-table responsive sticky-header="70vh" small :items="visibleItems" :fields="displayFields"
                 :current-page="currentPage" :tbody-tr-class="get_row_color"
                 :per-page="perPage" show-empty empty-text="No SOS requests for this filter."
                 hover @row-clicked="open_detail">

          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle"></b-spinner>
            </div>
          </template>

          <template slot="processed_time" slot-scope="row">
            {{row.item.processed_time | formatDateTimeSmallYear}}
          </template>

          <template slot="driver" slot-scope="row">
            <!-- received rows show the sending company's operator contact only; the driver's
                 name/phone is the source company's PII and is never shown to a partner -->
            <template v-if="direction === 'received'">
              <div v-if="operatorName(row.item)">{{ operatorName(row.item) }}</div>
              <a v-if="operatorPhone(row.item)" :href="'tel:' + operatorPhone(row.item)" @click.stop><i class="fa fa-phone"></i>&nbsp;{{ operatorPhone(row.item) }}</a>
              <a v-if="operatorEmail(row.item)" :href="'mailto:' + operatorEmail(row.item)" @click.stop class="sos-op-email">{{ operatorEmail(row.item) }}</a>
              <span v-if="!operatorName(row.item) && !operatorPhone(row.item) && !operatorEmail(row.item)" class="sos-muted">—</span>
            </template>
            <template v-else>
              <div v-if="driverName(row.item)">{{ driverName(row.item) }}</div>
              <a v-if="driverPhone(row.item)" :href="'tel:' + driverPhone(row.item)" @click.stop><i class="fa fa-phone"></i>&nbsp;{{ driverPhone(row.item) }}</a>
              <span v-if="!driverName(row.item) && !driverPhone(row.item)" class="sos-muted">—</span>
            </template>
          </template>

          <template slot="address" slot-scope="row">
            <span :title="addressOf(row.item)">{{ addressOf(row.item) || '—' }}</span>
          </template>

          <template slot="nearby" slot-scope="row">
            <div class="sos-nearby">
              <div v-for="g in nearbyGroups(row.item)" :key="g.key" class="sos-nearby-row">
                <span class="sos-nearby-num" :class="{ 'sos-nearby-num--zero': !g.count }">{{ g.count }}</span>
                <span class="sos-nearby-label">{{ g.label }}</span>
              </div>
            </div>
          </template>

          <template slot="recipients" slot-scope="row">
            <div class="sos-groups">
              <div v-for="g in recipientGroups(row.item)" :key="g.key" class="sos-group">
                <span class="sos-tag" :class="'sos-tag--' + g.key">{{ g.label }}</span>
                <span class="sos-group-vals"><span v-for="v in g.values" :key="v" class="sos-group-val">{{ v }}</span></span>
              </div>
            </div>
          </template>

          <template slot="sos_status" slot-scope="row">
            <span class="sos-status-cell">
              <span class="sos-card-status" :class="'sos-card-status--' + (row.item.sos_status || 'open')">{{ row.item.sos_status || 'open' }}</span>
              <i v-if="row.item.notification_failed" class="fa fa-exclamation-triangle sos-flag sos-flag--warn" title="No recipients configured — no alert was sent"></i>
              <i v-if="row.item.broadcast" class="fa fa-bullhorn sos-flag sos-flag--broadcast" title="Broadcast to partner companies"></i>
              <span v-if="responseCount(row.item)" class="sos-flag sos-flag--resp" :title="responseCount(row.item) + ' response(s)'"><i class="fa fa-reply"></i>&nbsp;{{ responseCount(row.item) }}</span>
            </span>
          </template>

          <template slot="actions" slot-scope="row">
            <div class="sos-actions">
              <!-- a received SOS belongs to the sender; a partner can only remove their own copy -->
              <b-button v-if="direction === 'received'" size="sm" variant="outline-danger" @click.stop="dismiss_request(row.item)" title="Remove from my list" aria-label="Remove from my list"><i class="fa fa-times"></i></b-button>
              <template v-else>
                <b-button size="sm" variant="outline-success" @click.stop="close_request(row.item)" title="Close request" aria-label="Close request"><i class="fa fa-check"></i></b-button>
                <b-button size="sm" variant="outline-secondary" @click.stop="cancel_request(row.item)" title="Cancel request" aria-label="Cancel request"><i class="fa fa-ban"></i></b-button>
                <b-button size="sm" variant="outline-danger" @click.stop="delete_request(row.item)" title="Delete request" aria-label="Delete request"><i class="fa fa-trash-o"></i></b-button>
              </template>
            </div>
          </template>

        </b-table>
      </div>

      <!-- mobile: cards -->
      <div class="d-md-none sos-cards">
        <div v-if="loading" class="text-center my-3"><b-spinner></b-spinner></div>
        <div v-else-if="!visibleItems.length" class="sos-cards-empty">No SOS requests for this filter.</div>
        <div v-for="item in pagedItems" :key="item.request_id" class="sos-card" @click="open_detail(item)">
          <div class="sos-card-top">
            <span class="sos-card-status" :class="'sos-card-status--' + (item.sos_status || 'open')">{{ item.sos_status || 'open' }}</span>
            <i v-if="item.notification_failed" class="fa fa-exclamation-triangle sos-flag sos-flag--warn" title="No recipients configured — no alert was sent"></i>
            <i v-if="item.broadcast" class="fa fa-bullhorn sos-flag sos-flag--broadcast" title="Broadcast to partner companies"></i>
            <span v-if="responseCount(item)" class="sos-flag sos-flag--resp"><i class="fa fa-reply"></i>&nbsp;{{ responseCount(item) }}</span>
            <span class="sos-card-time">{{ item.processed_time | formatDateTimeSmallYear }}</span>
          </div>
          <div class="sos-card-vehicle">{{ item.source_vehicle_name || 'SOS' }}</div>
          <div class="sos-card-company">{{ item.source_tenant_name }}</div>
          <!-- received: operator contact only (no source-company driver PII) -->
          <div v-if="direction === 'received'">
            <div v-if="operatorName(item) || operatorPhone(item) || operatorEmail(item)" class="sos-card-line">
              <i class="fa fa-headset"></i>&nbsp;{{ operatorName(item) || 'Operator' }}<template v-if="operatorPhone(item)"> · <a :href="'tel:' + operatorPhone(item)" @click.stop><i class="fa fa-phone"></i>&nbsp;{{ operatorPhone(item) }}</a></template>
            </div>
            <div v-if="operatorEmail(item)" class="sos-card-line"><a :href="'mailto:' + operatorEmail(item)" @click.stop><i class="fa fa-envelope"></i>&nbsp;{{ operatorEmail(item) }}</a></div>
          </div>
          <div v-else-if="driverName(item) || driverPhone(item)" class="sos-card-line">
            <i class="fa fa-user"></i>&nbsp;{{ driverName(item) || 'Driver' }}<template v-if="driverPhone(item)"> · <a :href="'tel:' + driverPhone(item)" @click.stop><i class="fa fa-phone"></i>&nbsp;{{ driverPhone(item) }}</a></template>
          </div>
          <div v-if="addressOf(item)" class="sos-card-line"><i class="fa fa-map-marker"></i>&nbsp;{{ addressOf(item) }}</div>
          <div v-if="item.request_notes" class="sos-card-notes">{{ item.request_notes }}</div>
          <div class="sos-card-meta">
            <span><i class="fa fa-bus"></i>&nbsp;{{ countOf(item.internal_vehicle_details) }} nearby</span>
            <span><i class="fa fa-map-marker"></i>&nbsp;{{ countOf(item.internal_yard_details) }} yards</span>
          </div>
          <div class="sos-card-actions">
            <b-button v-if="direction === 'received'" size="sm" variant="outline-danger" @click.stop="dismiss_request(item)"><i class="fa fa-times"></i>&nbsp;Remove from my list</b-button>
            <template v-else>
              <b-button size="sm" variant="outline-success" @click.stop="close_request(item)" title="Close request" aria-label="Close request"><i class="fa fa-check"></i></b-button>
              <b-button size="sm" variant="outline-secondary" @click.stop="cancel_request(item)" title="Cancel request" aria-label="Cancel request"><i class="fa fa-ban"></i></b-button>
              <b-button size="sm" variant="outline-danger" @click.stop="delete_request(item)" title="Delete request" aria-label="Delete request"><i class="fa fa-trash-o"></i></b-button>
            </template>
          </div>
        </div>
      </div>
      <b-row>
        <b-col>
          <b-pagination
              v-model="currentPage"
              :total-rows="items.length"
              :per-page="perPage"
              align="center"
          >
          </b-pagination>
        </b-col>
      </b-row>
    </div>

  </div>

</template>

<script>

import {delete_sos_by_token, cancel_sos_by_token, close_sos_by_token, get_sos_history} from '../networking/apis'
import {mapState} from "vuex";
import moment from 'moment'
import SosSettingsModal from './sos_settings_modal.vue'

export default {
  name: 'sos_list',
  components: {
    SosSettingsModal,
  },
  props: {
    // 'sent' (SOS this company raised), 'received' (from another company), or '' (both)
    direction: { type: String, default: '' },
  },

  data() {
    return {
      items: [],
      summary: [],
      loading: true,
      showInfo: false,
      showSettings: false,
      range: 'recent',
      statusFilter: 'all',
      statusChips: [
        { key: 'all', label: 'All' },
        { key: 'open', label: 'Open' },
        { key: 'closed', label: 'Closed' },
        { key: 'cancelled', label: 'Cancelled' },
      ],
      currentPage: 1,
      perPage: 100,
      fields: [
        { key: 'source_tenant_name', label: 'SOS Company', sortable: true},
        { key: 'source_vehicle_name', label: 'SOS Vehicle', sortable: true},
        { key: 'driver', label: 'Driver', sortable: false},
        { key: 'address', label: 'Address', sortable: false, thStyle: { minWidth: "180px" }},
        { key: 'request_notes', label: 'Request Notes', sortable: true},
        { key: 'processed_time', label:'Event Time', sortable: true},
        { key: 'nearby', label:'Nearby', sortable: false},
        { key: 'recipients', label:'Recipients', sortable: false, thStyle: { minWidth: "340px" }},
        { key: 'sos_status', label: 'Status', sortable: true},
        { key: 'actions', label: 'Actions', sortable: false},
      ],
    }
  },

  async created() {
    await this.load_data()
  },
  methods: {
    forward_alert(...args) {
      this.$emit('showAlert', ...args)
    },
    async load_data() {
      this.loading = true
      const to = moment().format('YYYY-MM-DD')
      const from = this.range === 'recent'
        ? moment().add(-6, 'months').format('YYYY-MM-DD')
        : '2015-01-01'
      try {
        const {value: { data = {} } } = await get_sos_history(this.user.tenant, from, to, this.direction)
        this.summary = data.summary || {}
        if (this.summary.timezone) moment.tz.setDefault(this.summary.timezone)
        this.items = (data.events || []).slice().sort((a, b) => (b.processed_time || '').localeCompare(a.processed_time || ''))
        this.currentPage = 1
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }

      this.loading = false
    },
    set_range(r) {
      if (this.range === r) return
      this.range = r
      this.load_data()
    },
    set_status(key) {
      this.statusFilter = key
      this.currentPage = 1
    },
    countOf(x) {
      return x ? x.length : 0
    },
    responseCount(item) {
      return (item && item.responses) ? item.responses.length : 0
    },
    driverName(item) {
      const d = item && item.source_driver_details
      if (!d) return ''
      if (typeof d === 'string') return d
      return d.DriverName || d.driver_name || d.name || ''
    },
    driverPhone(item) {
      const d = item && item.source_driver_details
      let p = ''
      if (d && typeof d === 'object') p = d.source_driver_phone || d.driver_phone || d.phone || ''
      // origination_number is the source company's callback — shown on sent/all rows so a
      // relief vehicle can reach the stranded driver (received rows show operator contact instead)
      if (!p) p = (item && item.origination_number) || ''
      return /\d{3}/.test(p) ? p : ''  // a real number, not a staff username used as origin
    },
    // operator (sending company) contact — the only contact a partner sees on a received SOS
    operatorName(item) {
      return (item && item.operator_contact_name) || ''
    },
    operatorPhone(item) {
      const p = (item && item.operator_contact_phone) || ''
      return /\d{3}/.test(p) ? p : ''
    },
    operatorEmail(item) {
      return (item && item.operator_contact_email) || ''
    },
    addressOf(item) {
      const g = item && item.source_gps_info
      return (g && g.reverseGeo && g.reverseGeo.formattedLocation) || ''
    },
    nearbyGroups(item) {
      const networkCounts = {} // ORG -> count, one line per association network present
      let publicYards = 0
      let fleetInt = this.countOf(item.internal_yard_details) // the company's own yards
      for (const r of (item.registered_yard_details || [])) {
        const y = r.target_yard || r
        if (y.tenant_id) {
          fleetInt++
        } else {
          const org = (y.organization || '').toString().trim()
          if (org) networkCounts[org.toUpperCase()] = (networkCounts[org.toUpperCase()] || 0) + 1
          else publicYards++
        }
      }
      const groups = [{ key: 'vehicles', label: 'Vehicles', count: this.countOf(item.internal_vehicle_details) }]
      Object.keys(networkCounts).sort().forEach(org => {
        groups.push({ key: 'net-' + org, label: org + ' Yards', count: networkCounts[org] })
      })
      groups.push({ key: 'fleetint', label: 'Fleet Int Yards', count: fleetInt })
      if (publicYards) groups.push({ key: 'public', label: 'Public Yards', count: publicYards })
      return groups
    },
    recipientGroups(item) {
      const split = s => (s || '').split(',').map(v => v.trim()).filter(Boolean)
      const groups = []
      const email = split(item.email_recipients)
      const sms = split(item.sms_recipients)
      if (email.length) groups.push({ key: 'email', label: 'Email', values: email })
      if (sms.length) groups.push({ key: 'sms', label: 'SMS', values: sms })
      if (!groups.length) groups.push({ key: 'none', label: 'None', values: ['-'] })
      return groups
    },
    open_detail(item) {
      if (!item || !item.request_id) return
      // received rows open the partner (receiver) page; everything else the sender page
      const name = (this.direction === 'received' || item.role === 'receiver')
        ? 'sos_received_detail' : 'sos_detail'
      this.$router.push({ name, query: { request_id: item.request_id } })
    },
    get_row_color(item, type) {
      if (!item || type !== 'row') return
      return item.status
    },
    // lifecycle actions are token-based now; the per-event share_token rides on each history row
    actionToken(item) {
      return (item && item.share_token) || null
    },
    async close_request(item) {
      const token = this.actionToken(item)
      if (!token) { this.$emit('showAlert', 'open the SOS to change its status', 'warning', 4000); return }
      this.$emit('showAlert', "closing request...", 'info')
      await close_sos_by_token(token)
      this.$emit('showAlert', "request has been closed", 'success', 5000)
      await this.load_data()
    },
    async cancel_request(item) {
      const token = this.actionToken(item)
      if (!token) { this.$emit('showAlert', 'open the SOS to change its status', 'warning', 4000); return }
      this.$emit('showAlert', "cancelling request...", 'info')
      await cancel_sos_by_token(token)
      this.$emit('showAlert', "request has been cancelled", 'success', 5000)
      await this.load_data()
    },
    async delete_request(item) {
      const token = this.actionToken(item)
      if (!token) { this.$emit('showAlert', 'open the SOS to delete it', 'warning', 4000); return }
      this.$emit('showAlert', "deleting request...", 'info')
      await delete_sos_by_token(token)
      this.$emit('showAlert', "request has been deleted", 'success', 5000)
      await this.load_data()
    },
    async dismiss_request(item) {
      // a received SOS is this company's own copy of another company's request; removing it clears it
      // from our list only — the sending company (and every other partner) still has theirs
      const token = this.actionToken(item)
      if (!token) { this.$emit('showAlert', 'open the SOS to remove it', 'warning', 4000); return }
      if (!window.confirm('Remove this SOS from your list? The sending company still has it.')) return
      this.$emit('showAlert', 'removing from your list...', 'info')
      await delete_sos_by_token(token)
      this.$emit('showAlert', 'removed from your list', 'success', 4000)
      await this.load_data()
    }
  },
  computed:{
    ...mapState({
      user: state => state.profile.user
    }),
    canManageSettings() {
      return this.user && (this.user.role === 'admin' || this.user.role === 'customer-admin')
    },
    pageTitle() {
      return this.direction === 'sent' ? 'SOS Sent History'
        : this.direction === 'received' ? 'SOS Received History'
        : 'SOS Events'
    },
    displayFields() {
      // 'received' events come from another company, so its name is the key column; on 'sent' the
      // company is always this tenant, so drop that column
      return this.fields.map(f => {
        if (f.key === 'source_tenant_name') {
          if (this.direction === 'received') return { ...f, label: 'From Company' }
          return null
        }
        // received rows show the sending company's operator contact in place of the driver
        if (f.key === 'driver' && this.direction === 'received') return { ...f, label: 'Operator Contact' }
        return f
      }).filter(Boolean)
    },
    visibleItems() {
      if (this.statusFilter === 'all') return this.items
      return this.items.filter(i => (i.sos_status || 'open') === this.statusFilter)
    },
    statusCounts() {
      const c = { all: this.items.length, open: 0, closed: 0, cancelled: 0 }
      this.items.forEach(i => {
        const s = i.sos_status || 'open'
        if (c[s] != null) c[s]++
      })
      return c
    },
    pagedItems() {
      const start = (this.currentPage - 1) * this.perPage
      return this.visibleItems.slice(start, start + this.perPage)
    },
  },
  watch: {
    // the sent/received routes reuse this component; reload when switching between them
    direction() {
      this.statusFilter = 'all'
      this.currentPage = 1
      this.load_data()
    },
    user: {
      deep: true,
      handler() {
        this.load_data()
      }
    }
  }
}
</script>

<style scoped>

.sos-history-body {
  padding: 16px;
}
.sos-history-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}
.sos-history-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8295a7;
}
.sos-chips {
  display: inline-flex;
  gap: 6px;
}
.sos-chip {
  border: 1px solid #cdd6de;
  background: #fff;
  color: #47566a;
  font-size: 13px;
  font-weight: 600;
  padding: 5px 14px;
  border-radius: 999px;
  cursor: pointer;
}
.sos-chip:hover {
  border-color: #23608f;
  color: #23608f;
}
.sos-chip--active {
  background: #23608f;
  border-color: #23608f;
  color: #fff;
}
.sos-chip-count {
  font-variant-numeric: tabular-nums;
  opacity: 0.7;
  margin-left: 2px;
}

/* table styled to match the ERM Log */
.sos-table >>> .table.b-table { font-size: 13px; border-collapse: collapse; }
.sos-table >>> .table.b-table th,
.sos-table >>> .table.b-table td {
  border-left: none !important; border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  padding: 6px 10px !important;
  vertical-align: top !important;
  text-align: left !important;
}
.sos-table >>> .table.b-table th:first-child,
.sos-table >>> .table.b-table td:first-child { padding-left: 14px !important; }
.sos-table >>> .table.b-table th:last-child,
.sos-table >>> .table.b-table td:last-child { padding-right: 14px !important; }
.sos-table >>> .table.b-table thead th {
  background: #f8f9fa;
  font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; color: #555;
  box-shadow: inset 0 -2px 0 0 #ccc;
}
.sos-table >>> .table.b-table tbody tr { cursor: pointer; }
.sos-table >>> .table.b-table tbody tr:nth-child(even) td { background-color: #f7f8fa; }
.sos-table >>> .table.b-table tbody tr:hover td { background-color: #f0f5ff; }

/* nested stat groups (Nearby, Recipients) - like the alert history page */
.sos-groups {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  line-height: 1.35;
}
.sos-group {
  display: grid;
  grid-template-columns: 66px minmax(0, 1fr);
  gap: 2px 6px;
  align-items: start;
}
.sos-tag {
  justify-self: start;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
  background: #eef1f3;
  color: #55606c;
}
.sos-tag--email, .sos-tag--vehicles { background: #e6eef7; color: #23608f; }
.sos-tag--sms { background: #e3f0e7; color: #2f7d4f; }
.sos-tag--yards { background: #eef1f3; color: #55606c; }
.sos-tag--external { background: #f5ebd9; color: #a4650e; }
.sos-group-vals {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.sos-group-val {
  overflow-wrap: anywhere;
}
.sos-actions {
  display: flex;
  gap: 4px;
  white-space: nowrap;
}
.sos-nearby {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.sos-nearby-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 13px;
}
.sos-nearby-num {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #23608f;
  min-width: 16px;
  text-align: right;
}
.sos-nearby-num--zero {
  color: #b9c2cb;
}
.sos-nearby-label {
  color: #55606c;
  white-space: nowrap;
}

.sos-msg {
  white-space: pre-wrap;
  word-break: break-word;
  vertical-align: top !important;
  text-align: left !important;
}
.sos-msg-table td {
  vertical-align: top !important;
  text-align: left !important;
}
tr:nth-child(even) {
  background-color: #f2f2f2;
}

/* mobile cards */
.sos-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.sos-cards-empty {
  color: #8295a7;
  padding: 12px 4px;
}
.sos-card {
  background: #fff;
  border: 1px solid #e6e9ec;
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
  text-align: left;
}
.sos-card:active {
  background: #fafbfc;
}
.sos-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.sos-card-status {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 999px;
}
.sos-card-status--open {
  background: #f6e4e0;
  color: #ae3f2e;
}
.sos-card-status--cancelled {
  background: #eef1f3;
  color: #6e7e8f;
}
.sos-card-time {
  font-size: 12px;
  color: #8295a7;
}
.sos-status-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.sos-flag {
  font-size: 12px;
}
.sos-flag--warn { color: #c0392b; }
.sos-flag--broadcast { color: #9a6b00; }
.sos-flag--resp {
  color: #2f7d4f;
  font-weight: 700;
  font-size: 11px;
}
/* on a mobile card the time is pushed to the right; keep the flags left with the status */
.sos-card-top .sos-card-time { margin-left: auto; }
.sos-card-vehicle {
  font-size: 17px;
  font-weight: 700;
  color: #1c2733;
}
.sos-card-company {
  font-size: 14px;
  color: #6e7e8f;
}
.sos-card-line {
  font-size: 13px;
  color: #55636f;
  margin-top: 3px;
}
.sos-card-line a { color: #23608f; text-decoration: none; }
.sos-op-email { display: block; overflow-wrap: anywhere; }
.sos-muted { color: #b6c0cc; }
.sos-card-notes {
  font-size: 14px;
  color: #47566a;
  margin-top: 6px;
}
.sos-card-meta {
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: #6e7e8f;
  margin-top: 8px;
}
.sos-card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}
.sos-card-msg {
  margin-top: 12px;
  border-top: 1px solid #eef1f3;
  padding-top: 8px;
}
.sos-card-msg-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8295a7;
  margin-top: 6px;
}

</style>

